import React, { useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { useAuth } from "../auth/AuthContext";
import { SearchBar } from "../components/SearchBar";

function boolLabel(active) {
  return active ? "Active" : "Inactive";
}

async function extractErrorMessage(response, fallback) {
  try {
    const text = await response.text();
    if (!text) return fallback;

    try {
      const parsed = JSON.parse(text);
      return parsed?.error || parsed?.message || fallback;
    } catch {
      if (text.startsWith("<!DOCTYPE")) return fallback;
      return text;
    }
  } catch {
    return fallback;
  }
}

function normalizeRows(rows) {
  if (!Array.isArray(rows)) return [];

  return [...rows].sort((a, b) => {
    const ao = Number(a?.display_order ?? 999);
    const bo = Number(b?.display_order ?? 999);
    if (ao !== bo) return ao - bo;
    return String(a?.indicator_key || "").localeCompare(
      String(b?.indicator_key || ""),
    );
  });
}

export default function TradeoffIndicatorsAdmin() {
  const { authFetch } = useAuth();

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState("");

  const [creating, setCreating] = useState(false);
  const [newKey, setNewKey] = useState("");
  const [newLabel, setNewLabel] = useState("");
  const [newActive, setNewActive] = useState(true);

  const [editingKey, setEditingKey] = useState(null);
  const [editingValues, setEditingValues] = useState({
    indicator_key: "",
    indicator_label: "",
    active: true,
  });
  const [workingKey, setWorkingKey] = useState(null);

  const loadItems = async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await authFetch("/api/auth/admin/tradeoff-indicators");
      if (!response.ok) {
        throw new Error(
          await extractErrorMessage(
            response,
            "Error loading tradeoff indicators",
          ),
        );
      }

      const data = await response.json();
      setItems(normalizeRows(data?.indicators));
    } catch (err) {
      console.error("Load tradeoff indicators error:", err);
      setError(err?.message || "Error loading tradeoff indicators");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadItems();
  }, []);

  const filtered = useMemo(() => {
    if (!search.trim()) return items;

    const term = search.trim().toLowerCase();
    return items.filter((item) => {
      return (
        String(item.indicator_key || "").toLowerCase().includes(term) ||
        String(item.indicator_label || "").toLowerCase().includes(term) ||
        String(item.display_order ?? "").toLowerCase().includes(term) ||
        boolLabel(!!item.active).toLowerCase().includes(term)
      );
    });
  }, [items, search]);

  const beginEdit = (item) => {
    setEditingKey(item.indicator_key);
    setEditingValues({
      indicator_key: String(item.indicator_key || ""),
      indicator_label: String(item.indicator_label || ""),
      active: !!item.active,
    });
  };

  const handleCreate = async () => {
    const indicatorKey = String(newKey || "").trim().toLowerCase();
    const indicatorLabel = String(newLabel || "").trim();

    if (!indicatorKey || !indicatorLabel) {
      await Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "indicator_key and indicator_label are required.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      setCreating(true);

      const response = await authFetch("/api/auth/admin/tradeoff-indicators", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          indicator_key: indicatorKey,
          indicator_label: indicatorLabel,
          active: newActive,
        }),
      });

      if (!response.ok) {
        throw new Error(
          await extractErrorMessage(
            response,
            "Error creating tradeoff indicator",
          ),
        );
      }

      const data = await response.json();
      const created = data?.indicator;
      if (created) {
        setItems((prev) => normalizeRows([created, ...prev]));
      }

      setNewKey("");
      setNewLabel("");
      setNewActive(true);

      await Swal.fire({
        icon: "success",
        title: "Created",
        text: "Tradeoff indicator created successfully.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Create tradeoff indicator error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error creating tradeoff indicator",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleSave = async (originalKey) => {
    const indicatorKey = String(editingValues.indicator_key || "")
      .trim()
      .toLowerCase();
    const indicatorLabel = String(editingValues.indicator_label || "").trim();

    if (!indicatorKey || !indicatorLabel) {
      await Swal.fire({
        icon: "warning",
        title: "Warning",
        text: "indicator_key and indicator_label are required.",
        confirmButtonColor: "#3085d6",
      });
      return;
    }

    try {
      setWorkingKey(originalKey);

      const response = await authFetch(
        `/api/auth/admin/tradeoff-indicators/${encodeURIComponent(originalKey)}`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            indicator_key: indicatorKey,
            indicator_label: indicatorLabel,
            active: !!editingValues.active,
          }),
        },
      );

      if (!response.ok) {
        throw new Error(
          await extractErrorMessage(
            response,
            "Error updating tradeoff indicator",
          ),
        );
      }

      const data = await response.json();
      const updated = data?.indicator;
      if (updated) {
        setItems((prev) =>
          normalizeRows(
            prev.map((item) =>
              String(item.indicator_key) === String(originalKey)
                ? updated
                : item,
            ),
          ),
        );
      }

      setEditingKey(null);
      await Swal.fire({
        icon: "success",
        title: "Saved",
        text: "Tradeoff indicator updated successfully.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Update tradeoff indicator error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error updating tradeoff indicator",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setWorkingKey(null);
    }
  };

  const handleDelete = async (indicatorKey) => {
    const confirm = await Swal.fire({
      icon: "warning",
      title: "Confirm",
      text: `Delete ${indicatorKey}?`,
      showCancelButton: true,
      confirmButtonText: "Yes",
      cancelButtonText: "No",
      confirmButtonColor: "#3085d6",
    });

    if (!confirm.isConfirmed) return;

    try {
      setWorkingKey(indicatorKey);

      const response = await authFetch(
        `/api/auth/admin/tradeoff-indicators/${encodeURIComponent(indicatorKey)}`,
        { method: "DELETE" },
      );

      if (!response.ok) {
        throw new Error(
          await extractErrorMessage(
            response,
            "Error deleting tradeoff indicator",
          ),
        );
      }

      setItems((prev) =>
        prev.filter((item) => String(item.indicator_key) !== String(indicatorKey)),
      );

      await Swal.fire({
        icon: "success",
        title: "Done",
        text: "Tradeoff indicator deleted.",
        confirmButtonColor: "#3085d6",
      });
    } catch (err) {
      console.error("Delete tradeoff indicator error:", err);
      await Swal.fire({
        icon: "error",
        title: "Error",
        text: err?.message || "Error deleting tradeoff indicator",
        confirmButtonColor: "#3085d6",
      });
    } finally {
      setWorkingKey(null);
    }
  };

  if (loading) {
    return (
      <div className="w-full p-6 flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading tradeoff indicators...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="w-full p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h3 className="text-red-800 font-semibold mb-2">Error</h3>
          <p className="text-red-700 mb-4">{error}</p>
          <button
            onClick={loadItems}
            className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full p-4 md:p-6 font-montserrat space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Tradeoff Indicators
        </h2>
        <p className="text-gray-600">
          Control which indicators can be selected in Results &gt; Tradeoff charts.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white rounded-lg shadow-sm p-4">
          <SearchBar
            value={search}
            onChange={setSearch}
            placeholder="Search by key, label, status or order..."
          />
        </div>

        <div className="bg-white rounded-lg shadow-sm p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input
              className="rounded border border-slate-300 px-3 py-2 text-sm"
              placeholder="indicator_key"
              value={newKey}
              onChange={(e) => setNewKey(e.target.value.toLowerCase())}
              maxLength={120}
            />
            <input
              className="rounded border border-slate-300 px-3 py-2 text-sm md:col-span-2"
              placeholder="Indicator label"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
            />
  
          </div>

          <div className="mt-3 flex items-center justify-between gap-4">
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                checked={newActive}
                onChange={(e) => setNewActive(e.target.checked)}
              />
              Active
            </label>

            <button
              onClick={handleCreate}
              disabled={creating}
              className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
            >
              {creating ? "Creating..." : "Add indicator"}
            </button>
          </div>
        </div>
      </div>

      <div className="min-h-0">
        <div className="bg-white rounded-lg shadow-sm h-full overflow-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
              <tr>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Key
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Label
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-sm font-semibold text-gray-700">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-gray-200">
              {filtered.map((item) => {
                const isEditing = editingKey === item.indicator_key;
                const isWorking = workingKey === item.indicator_key;

                return (
                  <tr key={item.indicator_key} className="hover:bg-gray-50 transition">
                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 min-w-[220px]">
                      {isEditing ? (
                        <input
                          className="rounded border border-slate-300 px-2 py-1 text-sm w-full"
                          value={editingValues.indicator_key}
                          onChange={(e) =>
                            setEditingValues((prev) => ({
                              ...prev,
                              indicator_key: e.target.value.toLowerCase(),
                            }))
                          }
                          maxLength={120}
                        />
                      ) : (
                        item.indicator_key
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700 min-w-[260px]">
                      {isEditing ? (
                        <input
                          className="rounded border border-slate-300 px-2 py-1 text-sm w-full"
                          value={editingValues.indicator_label}
                          onChange={(e) =>
                            setEditingValues((prev) => ({
                              ...prev,
                              indicator_label: e.target.value,
                            }))
                          }
                        />
                      ) : (
                        item.indicator_label
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm text-gray-700 w-[140px]">
                      {isEditing ? (
                        <label className="inline-flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={!!editingValues.active}
                            onChange={(e) =>
                              setEditingValues((prev) => ({
                                ...prev,
                                active: e.target.checked,
                              }))
                            }
                          />
                          <span>{boolLabel(!!editingValues.active)}</span>
                        </label>
                      ) : (
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            item.active
                              ? "bg-emerald-100 text-emerald-800"
                              : "bg-slate-200 text-slate-700"
                          }`}
                        >
                          {boolLabel(!!item.active)}
                        </span>
                      )}
                    </td>

                    <td className="px-6 py-4 text-sm">
                      {isEditing ? (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleSave(item.indicator_key)}
                            disabled={isWorking}
                            className="px-3 py-1.5 bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50"
                          >
                            Save
                          </button>
                          <button
                            onClick={() => setEditingKey(null)}
                            disabled={isWorking}
                            className="px-3 py-1.5 bg-slate-200 text-slate-700 rounded hover:bg-slate-300 disabled:opacity-50"
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => beginEdit(item)}
                            disabled={isWorking}
                            className="px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(item.indicator_key)}
                            disabled={isWorking}
                            className="px-3 py-1.5 bg-red-600 text-white rounded hover:bg-red-700 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}

              {filtered.length === 0 && (
                <tr>
                  <td colSpan={4} className="px-6 py-16 text-center text-slate-500">
                    No indicators found.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
